import SharedLocationDataContext from 'context/SharedLocationDataContext';
import React, { ReactElement, useContext } from 'react';
import { IOfficeObject, IPageLocationFields } from 'types/contentful';
import CTA from './Cta';
import { useFactua } from 'context/FactuaContext';

interface ScheduleAppointmentCtaProps {
  location: IPageLocationFields;
  classNames?: string;
  colorScheme?: 'Primary' | 'Primary2' | 'Primary3' | 'Outlined' | 'White';
  wrapperClasses?: string;
  linkStyle?: boolean;
  ctaTextSuffix?: string;
  text?: string;
  factua?: boolean;
  onClick?: (slug: string) => void;
  from?: string;
  providerId?: string;
}

const DEFAULT_TEXT = 'Schedule An Appointment';
const CALL_TO_SCHEDULE_TEXT = 'Call To Schedule An Appointment';
const DEFAULT_LINK_STYLES = 'text-tertiary-actual underline';

export default function ScheduleAppointmentCta({
  location,
  colorScheme,
  classNames = '',
  wrapperClasses,
  linkStyle,
  ctaTextSuffix = '',
  text,
  factua = false,
  onClick,
  from = '',
  providerId,
}: ScheduleAppointmentCtaProps): ReactElement {
  const { scheduleAppointmentCtaText } = useContext(SharedLocationDataContext);
  const callToSchedule = location.disableScheduling === true || !location.schedulingUrl;
  const isPatientPortalScheduleUrl = [
    '/guest/appointment',
    '/guest/core/appointment',
  ].some((path) => location.schedulingUrl?.includes(path));

  let slug = '';
  const buildOfficeObject = (location: IPageLocationFields): IOfficeObject => ({
    city: location?.city,
    state: location?.state,
    zip: location?.zipCode,
    phone: location?.phoneNumber,
    name: location?.name,
    addressLine1: location?.address1,
    _id: location?.officeUID || '',
    isOpen: true,
    distance: 0,
  });

  if (factua && isPatientPortalScheduleUrl && from === 'doctor') {
    const params = new URLSearchParams({
      providerId: providerId || '',
      office: JSON.stringify(buildOfficeObject(location)),
    });
    slug = `${location.schedulingUrl}?${params.toString()}`;
  } else if (factua && isPatientPortalScheduleUrl && from === 'doctorOnly') {
    slug = `${location.schedulingUrl}?providerId=${providerId}`;
  } else if (factua && isPatientPortalScheduleUrl && from === 'location') {
    const params = new URLSearchParams({
      office: JSON.stringify(buildOfficeObject(location)),
    });
    if (params) {
      slug = `${location.schedulingUrl}?${params.toString()}`;
    }
  } else {
    let url = location.schedulingUrl;

    // If factua is false, remove "/core" from schedulingUrl
    if (!factua && typeof url === 'string') {
      url = url.replace('/core', '');
    }

    slug = callToSchedule
      ? `tel:${location.phoneNumber}`
      : isPatientPortalScheduleUrl && url
      ? factua
        ? url
        : `${url}?location=${location.city}, ${location.state}${
            location.officeUID ? `&office=${location.officeUID}` : ''
          }`
      : url || '';
  }

  const openInNewTab = slug.startsWith('http');
  const actionText = text
    ? callToSchedule
      ? `Call To Schedule`
      : text
    : callToSchedule
    ? CALL_TO_SCHEDULE_TEXT
    : scheduleAppointmentCtaText || DEFAULT_TEXT;

  const actionTextWithSuffix = `${actionText} ${ctaTextSuffix}`;

  let target = '';

  if (openInNewTab === true) {
    target = '_blank';
  }

  const { openFactua } = useFactua();

  const schedCTA = linkStyle ? (
    <a href={slug} target={target} className={classNames || DEFAULT_LINK_STYLES} rel="noopener">
      {actionTextWithSuffix}
    </a>
  ) : factua && isPatientPortalScheduleUrl ? (
    <CTA
      slug={slug}
      colorScheme={colorScheme || 'Primary'}
      text={actionTextWithSuffix}
      openInNewTab={openInNewTab}
      classNames={classNames}
      onClick={() => {
        if (factua) {
          openFactua(slug);
        } else {
          onClick && onClick(slug);
        }
      }}
    />
  ) : (
    <CTA
      slug={slug}
      colorScheme={colorScheme || 'Primary'}
      text={actionTextWithSuffix}
      openInNewTab={openInNewTab}
      classNames={classNames}
    ></CTA>
  );

  return wrapperClasses ? <div className={wrapperClasses}>{schedCTA}</div> : schedCTA;
}
